import React, { createContext, useCallback, useContext, useState } from 'react';

import { getMeta } from '../../api';
import { useFetch } from '../../hooks';
import { setOrgId } from '../../utilities';
import { getAgents, getDatasources } from '../../api';
import { getCurrentConversations as getConversations } from '../../api';

type Cleanup = () => void;

type AppContextType = {
  loading: boolean;
  meta: AppMetaType;

  title: React.ReactNode | null;
  removeTitle: (id: string) => void;
  addTitle: (node: React.ReactNode) => Cleanup;

  refreshAgents: () => void;
  refreshDataSources: () => void;
  refreshConversations: () => void;
};

type TitleEntry = { id: string; node: React.ReactNode };

const AppContext = createContext<AppContextType | undefined>(undefined);

const defaultMeta: AppMetaType = {
  tenant: { id: '1', name: '' },
  conferenceAgentsTenantId: '',

  agents: [],
  conversations: [],
  sources: [],
};

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [titles, setTitles] = useState<TitleEntry[]>([]);

  const { loading, data, setData } = useFetch<AppMetaType>(getMeta, [], {
    onSuccess: data => setOrgId(data.tenant.id),
  });
  const { refresh: refreshAgents } = useFetch(getAgents, [], {
    skip: true,
    onSuccess: agents => setData(old => ({ ...old, agents }) as AppMetaType),
  });
  const { refresh: refreshDataSources } = useFetch(getDatasources, [], {
    skip: true,
    onSuccess: sources => setData(old => ({ ...old, sources }) as AppMetaType),
  });
  const { refresh: refreshConversations } = useFetch(getConversations, [], {
    skip: true,
    onSuccess: conversations => {
      return setData(old => ({ ...old, conversations }) as AppMetaType);
    },
  });

  const removeTitle = useCallback((id: string) => {
    setTitles(prev => prev.filter(entry => entry.id !== id));
  }, []);

  const addTitle = useCallback(
    (node: React.ReactNode) => {
      const id = crypto.randomUUID();
      setTitles(prev => [...prev, { id, node }]);
      return () => removeTitle(id);
    },
    [removeTitle]
  );

  const contextValue = React.useMemo<AppContextType>(
    () => ({
      loading,

      addTitle,
      removeTitle,

      refreshAgents,
      refreshDataSources,
      refreshConversations,

      meta: data || defaultMeta,
      title: titles.at(-1)?.node || null,
    }),
    [
      loading,
      data,
      titles,
      addTitle,
      removeTitle,
      refreshAgents,
      refreshDataSources,
      refreshConversations,
    ]
  );

  return (
    <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>
  );
};

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};
